/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useSyncOptionData } from "./hooks";
import { exportDMFormat } from "../../../../globals/constants";
import { Label } from "../../../ui/label";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { RadixSelectControl } from "../../../ui/radix-select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";

const SyncOptions: React.FC = () => {
    const {
        formData,
        setFormData,
        syncOptionStatus,
        submitHandler,
        sshCliEnabled,
        setSshCliEnabled,
        sshCliStatus,
        submitSshCliHandler,
    } = useSyncOptionData();

    const [isDeviceBackupEditMode, setIsDeviceBackupEditMode] = useState(false);
    const [isSshCliEditMode, setIsSshCliEditMode] = useState(false);
    const [formDataSnapshot, setFormDataSnapshot] = useState(formData);
    const [sshCliSnapshot, setSshCliSnapshot] = useState(sshCliEnabled);

    const isSyncSaving = syncOptionStatus.isLoading;
    const isSshSaving = sshCliStatus.isLoading;

    useEffect(() => {
        if (!isDeviceBackupEditMode) {
            setFormDataSnapshot(formData);
        }
    }, [formData, isDeviceBackupEditMode]);

    useEffect(() => {
        if (!isSshCliEditMode) {
            setSshCliSnapshot(sshCliEnabled);
        }
    }, [sshCliEnabled, isSshCliEditMode]);

    const selectedExportTypeLabel = useMemo(() => {
        return exportDMFormat.find((item) => item.value === formData.exportType)?.text || "-";
    }, [formData.exportType]);

    const isDeviceBackupDirty = useMemo(() => {
        return (
            formData.cmEnabled !== formDataSnapshot.cmEnabled ||
            formData.dbEnabled !== formDataSnapshot.dbEnabled ||
            formData.exportType !== formDataSnapshot.exportType
        );
    }, [formData, formDataSnapshot]);

    const isSshCliDirty = useMemo(() => sshCliEnabled !== sshCliSnapshot, [sshCliEnabled, sshCliSnapshot]);

    const handleEnterDeviceBackupEditMode = () => {
        setFormDataSnapshot(formData);
        setIsDeviceBackupEditMode(true);
    };

    const handleCancelDeviceBackupEditMode = () => {
        setFormData(formDataSnapshot);
        setIsDeviceBackupEditMode(false);
    };

    const handleSaveDeviceBackup = async () => {
        if (!isDeviceBackupDirty) {
            setIsDeviceBackupEditMode(false);
            return;
        }
        await submitHandler();
        setIsDeviceBackupEditMode(false);
    };

    const handleEnterSshCliEditMode = () => {
        setSshCliSnapshot(sshCliEnabled);
        setIsSshCliEditMode(true);
    };

    const handleCancelSshCliEditMode = () => {
        setSshCliEnabled(sshCliSnapshot);
        setIsSshCliEditMode(false);
    };

    const handleSaveSshCli = async () => {
        if (!isSshCliDirty) {
            setIsSshCliEditMode(false);
            return;
        }
        await submitSshCliHandler();
        setIsSshCliEditMode(false);
    };

    const renderValue = (value: string | boolean | undefined) => {
        if (typeof value === "boolean") return value ? "Yes" : "No";
        if (!value) return "-";
        return value;
    };

    const renderReadOnlyField = (label: string, value: string | boolean | undefined) => (
        <div className="flex flex-col gap-0.5">
            <span className="text-sm text-muted-foreground">{label}</span>
            <div className="text-sm font-semibold text-foreground">{renderValue(value)}</div>
        </div>
    );

    return (
        <div
            className="flex min-w-0 w-full flex-col overflow-x-hidden overflow-y-auto px-4 pt-5 pb-10 md:px-3"
            style={{ color: "var(--foreground)" }}
        >
            <Card>
                <CardHeader className="flex flex-col gap-6 space-y-0 p-3 pl-6 pr-6 pt-4 pb-0">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-1.5">
                            <CardTitle className="mb-0">Auto-Configuration</CardTitle>
                            <CardDescription>
                                Automatic sync for CM and device database backups.
                            </CardDescription>
                        </div>
                    </div>
                    <div className="-mx-6 border-t border-border/50" aria-hidden />
                </CardHeader>

                <CardContent className="pt-6">
                    <div className="flex flex-col gap-6">
                        <div className="space-y-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <h3 className="text-sm font-semibold text-foreground">Device Configuration Backup</h3>
                                {!isDeviceBackupEditMode ? (
                                    <Button
                                        type="button"
                                        onClick={handleEnterDeviceBackupEditMode}
                                        disabled={isSyncSaving}
                                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                                    >
                                        Edit
                                    </Button>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            disabled={isSyncSaving}
                                            onClick={handleCancelDeviceBackupEditMode}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={handleSaveDeviceBackup}
                                            disabled={isSyncSaving || !isDeviceBackupDirty}
                                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                                        >
                                            {isSyncSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Save
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <div className={`grid min-w-0 grid-cols-1 gap-x-16 md:grid-cols-2 ${isDeviceBackupEditMode ? "gap-y-6" : "gap-y-3"}`}>
                                <div className="flex min-w-0 flex-col gap-2">
                                    {isDeviceBackupEditMode ? (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id="cmEnabled"
                                                checked={formData.cmEnabled}
                                                onCheckedChange={(checked) =>
                                                    setFormData((prev) => ({ ...prev, cmEnabled: !!checked }))
                                                }
                                                className="m-0"
                                            />
                                            <Label htmlFor="cmEnabled" className="cursor-pointer">
                                                Device Configuration Backup Enabled
                                            </Label>
                                        </div>
                                    ) : (
                                        renderReadOnlyField("Device Configuration Backup Enabled", formData.cmEnabled)
                                    )}
                                </div>

                                <div className={`flex min-w-0 flex-col gap-2 ${isDeviceBackupEditMode ? "md:col-span-2" : ""}`}>
                                    {isDeviceBackupEditMode ? (
                                        <div className="flex max-w-md flex-col gap-1">
                                            <Label htmlFor="exportType" className="mb-1 text-sm font-medium">
                                                File Format
                                            </Label>
                                            <RadixSelectControl
                                                value={formData.exportType || "FLAT_XML"}
                                                options={exportDMFormat.map((item) => ({ value: item.value, label: item.text }))}
                                                onValueChange={(val) =>
                                                    setFormData((prev) => ({ ...prev, exportType: val }))
                                                }
                                            />
                                        </div>
                                    ) : (
                                        renderReadOnlyField("File Format", selectedExportTypeLabel)
                                    )}
                                </div>

                                <div className="flex min-w-0 flex-col gap-2">
                                    {isDeviceBackupEditMode ? (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id="dbEnabled"
                                                checked={formData.dbEnabled}
                                                onCheckedChange={(checked) =>
                                                    setFormData((prev) => ({ ...prev, dbEnabled: !!checked }))
                                                }
                                                className="m-0"
                                            />
                                            <Label htmlFor="dbEnabled" className="cursor-pointer">
                                                Device Database Backup Enabled
                                            </Label>
                                        </div>
                                    ) : (
                                        renderReadOnlyField("Device Database Backup Enabled", formData.dbEnabled)
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="border-t pt-6" style={{ borderColor: "color-mix(in srgb, var(--border) 50%, transparent)" }}>
                            <div className="space-y-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <h3 className="text-sm font-semibold text-foreground">File Server CLI Access</h3>
                                    {!isSshCliEditMode ? (
                                        <Button
                                            type="button"
                                            onClick={handleEnterSshCliEditMode}
                                            disabled={isSshSaving}
                                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                                        >
                                            Edit
                                        </Button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                disabled={isSshSaving}
                                                onClick={handleCancelSshCliEditMode}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="button"
                                                onClick={handleSaveSshCli}
                                                disabled={isSshSaving || !isSshCliDirty}
                                                className="bg-primary text-primary-foreground hover:bg-primary/90"
                                            >
                                                {isSshSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                                Save
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <div className="grid min-w-0 grid-cols-1 gap-x-16 gap-y-3 md:grid-cols-2">
                                    <div className="flex min-w-0 flex-col gap-2">
                                        {isSshCliEditMode ? (
                                            <div className="flex items-center gap-2">
                                                <Checkbox
                                                    id="sshCliEnabled"
                                                    checked={sshCliEnabled}
                                                    onCheckedChange={(checked) => setSshCliEnabled(!!checked)}
                                                    className="m-0"
                                                />
                                                <Label htmlFor="sshCliEnabled" className="cursor-pointer">
                                                    File Server CLI Access Enabled
                                                </Label>
                                            </div>
                                        ) : (
                                            renderReadOnlyField("File Server CLI Access Enabled", sshCliEnabled)
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
export default SyncOptions;